import React, { useState, useCallback, useMemo } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";

/**
 * Detecta el tipo MIME de una imagen desde su DataURL
 * @param {string} dataURL - DataURL de la imagen
 * @returns {string} - Tipo MIME (image/jpeg, image/png, etc.)
 */
const getMimeTypeFromDataURL = (dataURL) => {
    if (!dataURL || typeof dataURL !== 'string') return 'image/jpeg';
    const match = dataURL.match(/data:([^;]+);/);
    return match ? match[1] : 'image/jpeg';
};

/**
 * Componente para recortar imágenes con soporte para diferentes formatos
 * @param {Object} props
 * @param {string} props.image - DataURL de la imagen a recortar
 * @param {Function} props.onImageSave - Callback con la imagen recortada
 * @param {number} [props.quality=0.92] - Calidad de compresión (0-1)
 */
const ImageCrop = ({ image, onImageSave, quality = 0.92 }) => {
    const [cropper, setCropper] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Detectar el tipo MIME de la imagen original
    const mimeType = useMemo(() => {
        const detectedMime = getMimeTypeFromDataURL(image);
        // Usar JPEG para formatos que no soportan transparencia o para optimizar tamaño
        // PNG solo para imágenes que explícitamente lo necesiten
        if (detectedMime === 'image/png' || detectedMime === 'image/gif') {
            return 'image/png';
        }
        return 'image/jpeg';
    }, [image]);

    const handleCrop = useCallback(() => {
        if (!cropper) {
            console.error('Cropper no inicializado');
            return;
        }

        setIsProcessing(true);

        try {
            // Obtener el canvas recortado
            const croppedCanvas = cropper.getCroppedCanvas({
                // Mantener calidad alta
                imageSmoothingEnabled: true,
                imageSmoothingQuality: 'high',
            });

            if (!croppedCanvas) {
                console.error('No se pudo obtener el canvas recortado');
                setIsProcessing(false);
                return;
            }

            // Para JPEG, necesitamos un fondo blanco (no soporta transparencia)
            // Crear un nuevo canvas con fondo blanco si es JPEG
            let finalCanvas = croppedCanvas;
            
            if (mimeType === 'image/jpeg') {
                // Crear un nuevo canvas con fondo blanco
                const newCanvas = document.createElement('canvas');
                newCanvas.width = croppedCanvas.width;
                newCanvas.height = croppedCanvas.height;
                const ctx = newCanvas.getContext('2d');
                
                // Verificar que el contexto existe
                if (ctx) {
                    // Rellenar con fondo blanco
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);
                    // Dibujar la imagen recortada encima
                    ctx.drawImage(croppedCanvas, 0, 0);
                    finalCanvas = newCanvas;
                }
            }

            // Convertir a DataURL con el tipo MIME y calidad apropiados
            const croppedImage = finalCanvas.toDataURL(mimeType, quality);

            // Verificar que la imagen no está vacía
            if (!croppedImage || croppedImage === 'data:,') {
                console.error('La imagen recortada está vacía');
                setIsProcessing(false);
                return;
            }

            // Verificar que tiene datos válidos (más de solo el header)
            const base64Data = croppedImage.split(',')[1];
            if (!base64Data || base64Data.length < 100) {
                console.error('Los datos de la imagen son inválidos o muy pequeños');
                setIsProcessing(false);
                return;
            }

            onImageSave(croppedImage);
        } catch (error) {
            console.error('Error al recortar la imagen:', error);
        } finally {
            setIsProcessing(false);
        }
    }, [cropper, mimeType, quality, onImageSave]);

    return (
        <div>
            {image && (
                <Cropper
                    style={{ height: 300, width: "100%" }}
                    src={image}
                    viewMode={1}
                    guides={true}
                    cropBoxResizable={true}
                    dragMode="move"
                    background={true}
                    checkCrossOrigin={false}
                    checkOrientation={true}
                    autoCropArea={1}
                    responsive={true}
                    onInitialized={(instance) => setCropper(instance)}
                />
            )}
            <button 
                onClick={handleCrop} 
                className="btn btn-primary mt-3"
                disabled={isProcessing || !cropper}
            >
                {isProcessing ? 'Procesando...' : 'Guardar Imagen Recortada'}
            </button>
            <small className="d-block text-muted mt-1">
                Formato: {mimeType === 'image/jpeg' ? 'JPEG' : 'PNG'} | Calidad: {Math.round(quality * 100)}%
            </small>
        </div>
    );
};

export default ImageCrop;
